if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      this.teardownMwApoVisibility();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.stickyColumnObserver) this.stickyColumnObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.apoWatcher) this.apoWatcher.disconnect();
      if (this.ctaStateWatcher) this.ctaStateWatcher.disconnect();
      if (this.scheduleCtaRefresh) {
        this.productSection.removeEventListener('change', this.scheduleCtaRefresh, true);
        this.productSection.removeEventListener('input', this.scheduleCtaRefresh, true);
        this.productSection.removeEventListener('click', this.scheduleCtaRefresh, true);
      }
    }

    teardownMwApoVisibility() {
      if (this.stickyColumnObserver) {
        this.stickyColumnObserver.disconnect();
        this.stickyColumnObserver = null;
      }
      if (!this.updateMwApoVisibility) return;
      window.removeEventListener('scroll', this.updateMwApoVisibility);
      window.removeEventListener('resize', this.updateMwApoVisibility);
      this.updateMwApoVisibility = null;
    }

    bindEvents() {
      this.watchForMwApo();
      this.observePriceVisibility();
      this.bindCta();
      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Finds whichever Add to Cart control is actually the live/interactive one
     * right now, so the panel's own button can proxy a real click to it rather
     * than re-implementing cart submission, Mageworx validation, or Mulberry's
     * warranty modal. For Mageworx APO products, Mulberry (mulberry.js) hides
     * the original .mw-apo-add-to-cart-custom and shows a .mulberry-custom-atc
     * clone instead, so we pick whichever of the two is actually visible.
     */
    findLiveAddToCartControl() {
      if (this.mwApoContainer) {
        const candidates = this.productSection.querySelectorAll(
          '.mw-apo-add-to-cart-custom, .mulberry-custom-atc'
        );
        for (const candidate of candidates) {
          if (candidate.offsetParent !== null) return candidate;
        }
      }
      return this.productForm ? this.productForm.querySelector('[name="add"]') : null;
    }

    /**
     * True while the live Add to Cart control is mid-submission (Mulberry,
     * Mageworx, or the theme product-form). During that window the control is
     * often disabled, which would otherwise make the sticky CTA flip back to
     * its "View details" / "Choose options" label.
     */
    isControlBusy(control) {
      if (!control) return false;
      return (
        control.classList.contains('is-loading') ||
        control.classList.contains('mw-apo-in-progress') ||
        control.classList.contains('mulberry-is-processing') ||
        control.getAttribute('aria-busy') === 'true'
      );
    }

    isControlReady(control) {
      if (!control) return false;
      if (this.isControlBusy(control)) return true;
      return !control.disabled && control.getAttribute('aria-disabled') !== 'true';
    }

    /**
     * Wires up the panel's button to act like a real Add to Cart button when
     * possible: proxying a click to the live control (see
     * findLiveAddToCartControl) when it's enabled, or falling back to
     * scrolling down to the options/variants section when it isn't (options
     * still need to be selected).
     */
    bindCta() {
      this.ctaButton = this.querySelector('.sticky-atc-panel__cta');
      if (!this.ctaButton) return;

      this.ctaSelectLabel = this.ctaButton.querySelector('.sticky-atc-panel__cta-label--select');
      this.ctaAddLabel = this.ctaButton.querySelector('.sticky-atc-panel__cta-label--add');

      this.refreshCtaState = () => {
        const control = this.findLiveAddToCartControl();
        const busy = this.isControlBusy(control);
        const ready = this.isControlReady(control);

        this.ctaButton.classList.toggle('sticky-atc-panel__cta--ready', ready);
        this.ctaButton.classList.toggle('is-loading', busy);
        this.ctaButton.toggleAttribute('aria-busy', busy);
        if (this.ctaAddLabel) this.ctaAddLabel.hidden = !ready;
        if (this.ctaSelectLabel) this.ctaSelectLabel.hidden = ready;
      };

      this.ctaButton.addEventListener('click', () => {
        if (this.ctaButton.classList.contains('is-loading')) return;

        const control = this.findLiveAddToCartControl();
        const ready = this.isControlReady(control);

        if (ready) {
          // Optimistic spinner so the label doesn't flash "View" before the
          // live control marks itself busy.
          this.ctaButton.classList.add('is-loading');
          this.ctaButton.setAttribute('aria-busy', 'true');
          control.click();
          return;
        }

        const anchor = this.ctaButton.dataset.anchor;
        const anchorEl = anchor && document.querySelector(anchor);
        if (anchorEl) anchorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      this.refreshCtaState();

      // Keep the ready/not-ready state in sync as options are selected. Option
      // widgets don't always reflect state changes as attribute mutations
      // synchronously, so listen for interaction events too.
      this.scheduleCtaRefresh = () => window.requestAnimationFrame(this.refreshCtaState);
      this.productSection.addEventListener('change', this.scheduleCtaRefresh, true);
      this.productSection.addEventListener('input', this.scheduleCtaRefresh, true);
      this.productSection.addEventListener('click', this.scheduleCtaRefresh, true);

      this.ctaStateWatcher = new MutationObserver(this.scheduleCtaRefresh);
      this.ctaStateWatcher.observe(this.productSection, {
        attributes: true,
        subtree: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'style'],
      });
    }

    /**
     * Mageworx renders its options block (.mw-apo-container) asynchronously and
     * can re-render it (e.g. on variant change), so keep watching the DOM until
     * it shows up, and re-attach if it gets swapped out. Until/unless it's
     * found, the panel uses the default productForm-based visibility logic.
     */
    watchForMwApo() {
      const trySwitchToMwApo = () => {
        const mwApoContainer = this.productSection.querySelector('.mw-apo-container');
        if (!mwApoContainer) return;
        if (mwApoContainer === this.mwApoContainer) return;

        this.mwApoContainer = mwApoContainer;
        this.observeDefaultVisibility(false);
        this.observeMwApoVisibility(mwApoContainer);
      };

      this.observeDefaultVisibility(true);
      trySwitchToMwApo();

      this.apoWatcher = new MutationObserver(trySwitchToMwApo);
      this.apoWatcher.observe(this.productSection, { childList: true, subtree: true });
    }

    /**
     * Sticky ATC stays hidden until the on-page price has scrolled out of view
     * above the viewport. Existing show/hide logic still applies after that.
     */
    observePriceVisibility() {
      if (this.priceObserver) {
        this.priceObserver.disconnect();
        this.priceObserver = null;
      }

      const priceEl = this.productSection.querySelector('.product-info__price');
      this.priceOutOfView = !priceEl;
      if (!priceEl) return;

      this.priceObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== priceEl) return;
          this.priceOutOfView = entry.boundingClientRect.bottom <= 0;
          this.updatePanelVisibility();
        });
      });
      this.priceObserver.observe(priceEl);
    }

    updatePanelVisibility() {
      if (this.mwApoContainer) {
        this.classList.toggle(
          'sticky-atc-panel--out',
          !(this.priceOutOfView && (this.mwApoContainerShow || this.pastStickyColumnShow))
        );
        return;
      }

      this.classList.toggle(
        'sticky-atc-panel--out',
        !(this.priceOutOfView && this.productFormOutOfView)
      );
    }

    /**
     * Always observe the buy form, not .product-info. On desktop, stick_on_scroll sets
     * --sticky-height on .product-info so that column stays intersecting and the panel
     * would never leave sticky-atc-panel--out when watching product-info.
     */
    observeDefaultVisibility(start) {
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }
      if (!start || !this.productForm) return;

      this.productFormOutOfView = false;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm) {
            this.productFormOutOfView = entry.boundingClientRect.bottom <= 0;
            this.updatePanelVisibility();
          }
        });
      });
      this.visibilityObserver.observe(this.productForm);
    }

    /**
     * For Mageworx APO products, show the sticky panel when EITHER:
     *  - .mw-apo-container is in view and its bottom edge hasn't yet reached
     *    the viewport (options list still extends below what's visible), or
     *  - the bottom of .product-info__sticky (the whole sticky buy-box column,
     *    when stick_on_scroll is enabled) has scrolled out of view above the
     *    viewport — so the panel stays available once you've scrolled past the
     *    entire buy box, even deep into content further down the page.
     * Hidden otherwise (e.g. before either has been reached, or once the
     * bottom of the options is visible but you haven't yet scrolled past the
     * whole sticky column).
     */
    observeMwApoVisibility(mwApoContainer) {
      this.teardownMwApoVisibility();

      this.mwApoContainerShow = false;
      this.pastStickyColumnShow = false;

      this.updateMwApoVisibility = () => {
        const rect = mwApoContainer.getBoundingClientRect();
        const bottomInView = rect.bottom <= window.innerHeight;
        this.mwApoContainerShow = rect.bottom > 0 && rect.top < window.innerHeight && !bottomInView;
        this.updatePanelVisibility();
      };

      this.visibilityObserver = new IntersectionObserver(this.updateMwApoVisibility);
      this.visibilityObserver.observe(mwApoContainer);

      // The IntersectionObserver only fires on intersection-ratio changes, but
      // the "has the bottom edge entered the viewport" check can flip on plain
      // scroll/resize without the intersection ratio itself changing, so keep
      // it in sync there too.
      window.addEventListener('scroll', this.updateMwApoVisibility, { passive: true });
      window.addEventListener('resize', this.updateMwApoVisibility);

      const productInfoSticky = this.productSection.querySelector('.product-info__sticky');
      if (productInfoSticky) {
        this.stickyColumnObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== productInfoSticky) return;
            this.pastStickyColumnShow = entry.boundingClientRect.bottom <= 0;
            this.updatePanelVisibility();
          });
        });
        this.stickyColumnObserver.observe(productInfoSticky);
      }
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
